import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import Slider from "components/DesignSystem/Slider/Slider";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useGetPersonaRoleHistoryQuery } from "store/apis/directorsAndOfficers";
import { Person, PersonRole } from "types/Models/apAutofills";
import { formatDate } from "utils/formatDate";
import { PositionHistoryIcon } from "components/icons/PositionHistoryIcon";
import { EditDetailsIcon } from "components/icons/EditDetailsIcon";

const roleLabel = (role: PersonRole) => {
  if (role.persona_type === "OFFICER") {
    return role.persona_role?.name || role.designation || "Officer";
  }
  if (role.persona_type === "SHAREHOLDER") {
    return "Shareholder";
  }
  return "Director";
};

export const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="t-flex t-w-full t-flex-col t-gap-4 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4">
    <div className="t-text-subtext t-text-text-100">{title}</div>
    <div className="t-h-px t-w-full t-bg-neutral-10" />
    {children}
  </div>
);

export const DetailField = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) => (
  <div className="t-flex t-flex-1 t-flex-col t-gap-1">
    <div className="t-text-body-sm t-text-text-30">{label}</div>
    <div className="t-text-body t-text-text-100">{value}</div>
  </div>
);

export const PersonSlider = ({
  person,
  onEdit,
}: {
  person: Person;
  onEdit?: () => void;
}) => {
  const { entityId } = useParams<{ entityId: string }>();
  const personName = `${person.first_name} ${person.last_name}`;
  const shareholderRole = person.roles.find(
    (role) => role.persona_type === "SHAREHOLDER"
  );

  const { data: historyData, isLoading: isHistoryLoading } =
    useGetPersonaRoleHistoryQuery(
      { entityId, personaAutofillId: person.persona_autofill_id },
      { skip: !entityId || !person.persona_autofill_id }
    );
  const history = historyData?.history || [];

  return (
    <>
      <Slider.Header>
        <Slider.Title>Person details</Slider.Title>
        <Slider.Close />
      </Slider.Header>
      <Slider.Body>
        <div className="t-flex t-flex-col t-gap-4">
          <SectionCard title="Details">
            <div className="t-flex t-w-full t-flex-col t-gap-6">
              <DetailField label="Full name" value={personName} />
              <div className="t-flex t-w-full t-gap-4">
                <DetailField
                  label="Email address"
                  value={person.email || "-"}
                />
                <DetailField
                  label="Phone number"
                  value={person.phone_number || "-"}
                />
              </div>
              <div className="t-flex t-w-full t-gap-4">
                <DetailField
                  label="Country of residence"
                  value={person.country_of_residence || "-"}
                />
                {shareholderRole && (
                  <DetailField
                    label="Tax residency"
                    value={shareholderRole.shareholder_country || "-"}
                  />
                )}
              </div>
              <DetailField
                label="Residential address"
                value={person.address || "-"}
              />
              <DetailField
                label="Authorised signatory"
                value={person.is_authorised_signatory ? "Yes" : "No"}
              />
            </div>
          </SectionCard>

          <SectionCard title="Current Roles">
            <div className="t-flex t-w-full t-flex-col">
              {person.roles.map((role, index) => (
                <div
                  key={role.join_id}
                  className={`t-flex t-w-full t-items-center t-justify-between t-gap-2 t-p-4 ${
                    index !== person.roles.length - 1
                      ? "t-border-0 t-border-b t-border-solid t-border-neutral-10"
                      : ""
                  }`}
                >
                  <div className="t-flex t-flex-1 t-flex-col t-gap-1">
                    <div className="t-flex t-items-center t-gap-2">
                      <span className="t-text-subtext t-text-text-60">
                        {roleLabel(role)}
                      </span>
                    </div>
                    {role.persona_type === "SHAREHOLDER" &&
                      role.ownership_stake && (
                        <div className="t-flex t-items-center t-gap-1">
                          <span className="t-text-body t-text-text-30">
                            Ownership:
                          </span>
                          <span className="t-text-body t-text-text-100">
                            {role.ownership_stake}%
                          </span>
                        </div>
                      )}
                    {role.appointed_on && (
                      <div className="t-flex t-items-center t-gap-1">
                        <span className="t-text-body t-text-text-30">
                          Start date:
                        </span>
                        <span className="t-text-body t-text-text-100">
                          {formatDate(role.appointed_on)}
                        </span>
                      </div>
                    )}
                    {role.resigned_on && (
                      <div className="t-flex t-items-center t-gap-1">
                        <span className="t-text-body t-text-text-30">
                          End date:
                        </span>
                        <span className="t-text-body t-text-text-100">
                          {formatDate(role.resigned_on)}
                        </span>
                      </div>
                    )}
                  </div>
                  <Tag
                    tagType={role.ap_status === "ACTIVE" ? "green" : "gray"}
                    size="small"
                    icon={false}
                    rounded
                  >
                    {role.ap_status === "ACTIVE" ? "Active" : "Former"}
                  </Tag>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Position History">
            {isHistoryLoading && <Loader />}
            {!isHistoryLoading && history.length === 0 && (
              <div className="t-text-body-sm t-text-text-30">
                No history recorded yet.
              </div>
            )}
            <div className="t-flex t-w-full t-flex-col t-gap-6">
              {history.map((entry, index) => (
                <div key={entry.uuid} className="t-flex t-w-full t-gap-2">
                  <div className="t-flex t-flex-col t-items-center">
                    <PositionHistoryIcon />
                    {index !== history.length - 1 && (
                      <div className="t-mt-1 t-w-px t-flex-1 t-bg-neutral-10" />
                    )}
                  </div>
                  <div className="t-flex t-flex-1 t-flex-col t-gap-1">
                    <div className="t-text-subtext t-text-text-60">
                      {entry.narrative}
                    </div>
                    <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-neutral-50">
                      {entry.actor && <span>by {entry.actor}</span>}
                      {entry.actor && (
                        <span className="t-h-1 t-w-1 t-flex-shrink-0 t-rounded-full t-bg-neutral-50" />
                      )}
                      <span>{formatDate(entry.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </Slider.Body>
      {onEdit && (
        <Slider.Footer>
          <Button
            type="button"
            customType="secondary"
            onClick={onEdit}
            className="!t-h-10 t-w-full !t-justify-center !t-rounded-md !t-border-neutral-10 !t-bg-white !t-text-button !t-text-text-60 !t-shadow-xs"
          >
            <span className="t-inline-flex t-items-center t-gap-1">
              <EditDetailsIcon />
              Edit details
            </span>
          </Button>
        </Slider.Footer>
      )}
    </>
  );
};
